import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Card,
  Select,
  Row,
  Col,
  Typography,
  Spin,
  Button,
  Form,
  Radio,
  Divider,
  Space,
  Statistic,
  Modal,
  Input,
  Checkbox,
} from "antd";
import {
  BarChartOutlined,
  SearchOutlined,
  ReloadOutlined,
  CodeOutlined,
  CopyOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { request } from "../../functions/request";
import { Content } from "antd/es/layout/layout";
import { toast } from "react-toastify";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from "react-router-dom";
import PainelRankingComex from "./comexRanking/PainelRankingComex";
import { specDoGraficoComex } from "./comexGerador";
import { CHAVE_SPEC_IMPORTADO } from "../Graficos/GeradorGraficos";

const { Title, Text } = Typography;

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1997 + 1 }, (_, i) => currentYear - i);

const months = [
  { value: 1, label: "Janeiro", short: "Jan" },
  { value: 2, label: "Fevereiro", short: "Fev" },
  { value: 3, label: "Março", short: "Mar" },
  { value: 4, label: "Abril", short: "Abr" },
  { value: 5, label: "Maio", short: "Mai" },
  { value: 6, label: "Junho", short: "Jun" },
  { value: 7, label: "Julho", short: "Jul" },
  { value: 8, label: "Agosto", short: "Ago" },
  { value: 9, label: "Setembro", short: "Set" },
  { value: 10, label: "Outubro", short: "Out" },
  { value: 11, label: "Novembro", short: "Nov" },
  { value: 12, label: "Dezembro", short: "Dez" },
];

const metricOptions = [
  { value: "valor_fob_usd", label: "Valor FOB" },
  { value: "quilograma_liquido", label: "Volume (Milhão/T)" },
  { value: "quantidade_estatistica", label: "Quantidade Estatística" },
  { value: "valor_medio_ton", label: "Valor Médio (US$/ton | R$/ton)" },
  { value: "valor_medio_qtd", label: "Valor Médio por quantidade (US$/cab | R$/cab)" },
];

/**
 * Unidade da quantidade estatística: cabeça nos animais vivos (é como o
 * mercado conta e como a Receita registra), unidade genérica no resto.
 */
export function unidadeDaQuantidade(commodities) {
  return commodities.length && commodities.every((c) => /animais vivos/i.test(c)) ? "cab" : "un";
}

const fluxoOptions = [
  { value: "", label: "Todos" },
  { value: "Exportação", label: "Exportação" },
  { value: "Importação", label: "Importação" },
];

function scaledValue(metric, value) {
  if (metric === "quilograma_liquido") {
    // O banco guarda quilo; o gráfico trabalha em tonelada.
    return value / 1000;
  }
  return value;
}

/**
 * A escala do gráfico, decidida UMA vez pelo maior valor plotado.
 *
 * Antes cada pedaço decidia a sua: o rótulo do ponto dividia por mil de
 * novo (sobre um valor que já vinha em tonelada), o eixo usava outro
 * corte e o subtítulo trazia "Milhão/T" fixo, valesse o que valesse. O
 * resultado era um gráfico de 207 mil toneladas anunciando 207 milhões.
 *
 * Agora o divisor e a unidade saem daqui e todo mundo usa os dois: o
 * número aparece limpo no rótulo e a grandeza fica escrita na legenda,
 * que é como os gráficos da casa sempre foram feitos.
 */
export function escalaDaMetrica(metric, maiorValor, moeda, unidadeQtd = "un") {
  const maximo = Math.abs(Number(maiorValor) || 0);
  const dinheiro = moeda === "brl" ? "R$" : "US$";
  const cab = unidadeQtd === "cab";

  switch (metric) {
    case "valor_fob_usd":
      if (maximo >= 1e9) return { divisor: 1e9, unidade: `${dinheiro} bilhões` };
      if (maximo >= 1e6) return { divisor: 1e6, unidade: `${dinheiro} milhões` };
      if (maximo >= 1e3) return { divisor: 1e3, unidade: `${dinheiro} mil` };
      return { divisor: 1, unidade: dinheiro };

    case "quilograma_liquido":
      // Valores já em tonelada (ver scaledValue).
      if (maximo >= 1e6) return { divisor: 1e6, unidade: "milhão/t" };
      if (maximo >= 1e3) return { divisor: 1e3, unidade: "mil/t" };
      return { divisor: 1, unidade: "t" };

    case "quantidade_estatistica":
      if (maximo >= 1e6) return { divisor: 1e6, unidade: cab ? "milhões de cab" : "milhões" };
      if (maximo >= 1e3) return { divisor: 1e3, unidade: cab ? "mil cab" : "mil" };
      return { divisor: 1, unidade: cab ? "cab" : "unidades" };

    // Média por tonelada não se escala: o número já é da ordem de
    // grandeza que o leitor espera, e dividir esconderia o preço.
    case "valor_medio_ton":
      return { divisor: 1, unidade: `${dinheiro}/ton` };
    case "valor_medio_qtd":
      return { divisor: 1, unidade: `${dinheiro}/${unidadeQtd}` };

    default:
      return { divisor: 1, unidade: "" };
  }
}

/** A escala de um conjunto de dados do gráfico, pelo maior valor plotado. */
function escalaDosDados(dados, metric, moeda, unidadeQtd) {
  const valores = !dados ? [] : dados.multiSeries ? dados.series.flatMap((s) => s.data || []) : dados.values || [];
  const maior = valores.length ? Math.max(...valores.map((v) => Math.abs(v || 0))) : 0;
  return escalaDaMetrica(metric, maior, moeda, unidadeQtd);
}

/**
 * Pacote gráfico: as variações que saem de uma consulta só, na ordem em que
 * entram na matéria (como no "Confira o pacote gráfico..." dos alertas).
 * As métricas são as da base "peso"; na base "quantidade" (animais vivos)
 * o volume e o valor médio trocam para cabeças — ver metricaNaBase.
 */
export const VARIACOES_PACOTE = [
  { chave: "volume", rotulo: "Volume", metrica: "quilograma_liquido", moeda: "usd" },
  { chave: "fob_usd", rotulo: "Faturamento US$", metrica: "valor_fob_usd", moeda: "usd" },
  { chave: "fob_brl", rotulo: "Faturamento R$", metrica: "valor_fob_usd", moeda: "brl" },
  { chave: "medio_usd", rotulo: "Valor médio US$", metrica: "valor_medio_ton", moeda: "usd" },
  { chave: "medio_brl", rotulo: "Valor médio R$", metrica: "valor_medio_ton", moeda: "brl" },
];

/** "0817600 - PORTO DE SANTOS" -> "PORTO DE SANTOS". */
export function nomeDaUrf(urf) {
  return String(urf || "").replace(/^\d+\s*-\s*/, "").trim();
}

/** "PORTO DE SANTOS" -> "Porto de Santos", para o título do gráfico. */
export function nomeBonito(texto) {
  const minusculas = new Set(["de", "da", "do", "das", "dos", "e"]);
  return String(texto || "")
    .toLowerCase()
    .split(/\s+/)
    .map((p, i) => (i > 0 && minusculas.has(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(" ");
}

/** Métrica da variação do pacote na base escolhida: peso (t) ou quantidade. */
export function metricaNaBase(metrica, base) {
  if (base !== "quantidade") return metrica;
  if (metrica === "quilograma_liquido") return "quantidade_estatistica";
  if (metrica === "valor_medio_ton") return "valor_medio_qtd";
  return metrica;
}

/** Número no padrão brasileiro, já dividido pela escala do gráfico. */
function formatarNaEscala(valor, divisor, casas = 2) {
  const n = (Number(valor) || 0) / divisor;
  return n.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export const MAX_COMMODITIES = 3;
export const SEP_TIPO = "::";

/**
 * Junta os dados de cada commodity num gráfico só.
 *
 * Com uma commodity o resultado é exatamente o de antes. Com mais de uma:
 * - agrupamentos de série única (ano, mês, meses escolhidos) viram uma série
 *   por commodity, alinhadas pela chave do período — uma commodity pode ter
 *   anos que a outra não tem, e aí o ponto dela vale 0 (sem comércio);
 * - agrupamentos que já separam por ano (ano/mês, trimestre, semestre)
 *   viram "Commodity Ano" para cada combinação.
 */
export function combinarCommodities(resultados) {
  if (resultados.length === 1) return resultados[0].dados;
  const primeiro = resultados[0].dados;

  if (primeiro.multiSeries) {
    return {
      categories: primeiro.categories,
      metricKey: primeiro.metricKey,
      multiSeries: true,
      porCommodity: true,
      series: resultados.flatMap(({ nome, dados }) =>
        dados.series.map((s) => ({ name: `${nome} ${s.name}`, data: s.data }))
      ),
    };
  }

  const rotuloPorChave = new Map();
  for (const { dados } of resultados) {
    (dados.chaves || dados.categories).forEach((chave, i) => {
      if (!rotuloPorChave.has(chave)) rotuloPorChave.set(chave, dados.categories[i]);
    });
  }
  const chaves = [...rotuloPorChave.keys()].sort((a, b) => String(a).localeCompare(String(b)));

  return {
    categories: chaves.map((c) => rotuloPorChave.get(c)),
    chaves,
    metricKey: primeiro.metricKey,
    multiSeries: true,
    porCommodity: true,
    series: resultados.map(({ nome, dados }) => {
      const indice = new Map((dados.chaves || dados.categories).map((c, i) => [c, i]));
      return { name: nome, data: chaves.map((c) => (indice.has(c) ? dados.values[indice.get(c)] : 0)) };
    }),
  };
}

export default function ComexGraficos() {
  const [form] = Form.useForm();

  // Filtros
  const [anoInicio, setAnoInicio] = useState(currentYear - 5);
  const [anoFim, setAnoFim] = useState(currentYear);
  const [mesSelecionado, setMesSelecionado] = useState([]);
  const [fluxo, setFluxo] = useState("Exportação");
  const [metrica, setMetrica] = useState("valor_fob_usd");
  const [paisSelecionado, setPaisSelecionado] = useState([]);
  const [ufSelecionada, setUfSelecionada] = useState([]);
  // Até MAX_COMMODITIES por gráfico, cada uma vira uma série. Os tipos são
  // guardados como "commodity::tipo" porque há nomes repetidos entre
  // commodities ("In natura" existe em carne bovina, suína e outras).
  const [commoditiesSelecionadas, setCommoditiesSelecionadas] = useState([]);
  const [tiposSelecionados, setTiposSelecionados] = useState([]);
  // Com 2+ commodities: somar numa série só em vez de uma série por commodity.
  const [somarCommodities, setSomarCommodities] = useState(false);
  // Última consulta, para ligar/desligar a soma sem buscar de novo.
  const ultimaBusca = useRef(null);
  // Continentes completos (a Ásia inclui o Oriente Médio) e blocos: o
  // destino é a união dos países de todos os escolhidos.
  const [continenteSelecionado, setContinenteSelecionado] = useState([]);
  // Um ou mais blocos: o filtro é a união dos países deles.
  const [blocoSelecionado, setBlocoSelecionado] = useState([]);
  const [regiaoSelecionada, setRegiaoSelecionada] = useState(null);
  // Guarda o NOME da URF; a consulta leva todos os códigos com esse nome
  // (ver codigosDasUrfs).
  const [urfSelecionada, setUrfSelecionada] = useState([]);
  const [agrupar, setAgrupar] = useState("ano"); // ano, mes, ano_mes, trimestre, semestre
  const [tipoGrafico, setTipoGrafico] = useState("area"); // area, bar
  const [moeda, setMoeda] = useState("usd"); // usd, brl
  const [ocultarLegendaX, setOcultarLegendaX] = useState(false);
  const [chartColor, setChartColor] = useState("#39FF14");
  const [chartCols, setChartCols] = useState(16);
  // "serie" = evolução no tempo (o gráfico de sempre); "ranking" = imagem de
  // maiores exportadores/compradores, no formato da casa.
  const [visao, setVisao] = useState("serie");
  const ehPopup = useMemo(() => new URLSearchParams(window.location.search).get("popup") === "1", []);

  // Dados dos índices (filtros)
  const [paises, setPaises] = useState([]);
  const [estados, setEstados] = useState([]);
  const [ncms, setNcms] = useState([]);
  const [urfs, setUrfs] = useState([]);
  const [loadingIndices, setLoadingIndices] = useState(false);

  // Dados do gráfico
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Pacote: vários gráficos (variações de métrica/moeda) numa consulta só.
  const [pacote, setPacote] = useState(false);
  const [variacoesPacote, setVariacoesPacote] = useState(VARIACOES_PACOTE.map((v) => v.chave));
  const [pacoteGraficos, setPacoteGraficos] = useState(null);
  // Base do volume no pacote: peso (t) ou quantidade estatística (cabeças
  // nos animais vivos) — muda o volume e o valor médio.
  const [baseVolume, setBaseVolume] = useState("peso");
  const navigate = useNavigate();

  // Carregar índices na montagem
  useEffect(() => {
    loadIndices();
  }, []);

  const loadIndices = async () => {
    setLoadingIndices(true);
    try {
      const [paisesRes, estadosRes, ncmsRes, urfsRes] = await Promise.all([
        request("/comex-consulta/paises", { method: "GET" }),
        request("/comex-consulta/estados", { method: "GET" }),
        request("/comex-consulta/ncm", { method: "GET" }),
        request("/comex-consulta/urfs", { method: "GET" }),
      ]);
      setPaises(paisesRes || []);
      setEstados(estadosRes || []);
      setNcms(ncmsRes || []);
      setUrfs(urfsRes || []);
    } catch (error) {
      console.error("Erro ao carregar índices:", error);
      toast.error("Erro ao carregar filtros. Verifique a conexão.");
    } finally {
      setLoadingIndices(false);
    }
  };

  // Extrair blocos únicos dos países
  // Há um bloco gravado como "América Central<TAB>América Central e Caribe":
  // fica só a parte depois do TAB, que é o bloco de verdade — sem isso ele
  // aparecia como uma opção a mais, com um único país.
  // Ordem alfabética de gente, não de tabela ASCII: o sort() puro joga
  // "África" e "Ásia" para o fim da lista, depois do "U" de União Europeia.
  const emOrdem = (lista) => [...lista].sort((a, b) => a.localeCompare(b, "pt-BR"));

  const blocosUnicos = emOrdem(
    new Set(paises.map((p) => p.bloco && p.bloco.split("\t").pop().trim()).filter(Boolean))
  );
  const regioesUnicas = emOrdem(new Set(estados.map((e) => e.regiao).filter(Boolean)));
  const continentesUnicos = emOrdem(new Set(paises.map((p) => p.continente).filter(Boolean)));

  // A mesma URF aparece com códigos diferentes da Receita (alfândega e
  // inspetoria: "0140152 - PORTO MURTINHO" e "0145352 - PORTO MURTINHO"),
  // e a tela mostrava o nome repetido sem diferença. Agora é uma opção por
  // nome, e a consulta leva todos os códigos dela.
  const urfsPorNome = useMemo(() => {
    const mapa = new Map();
    for (const u of urfs) {
      const nome = nomeDaUrf(u.urf);
      if (!mapa.has(nome)) mapa.set(nome, []);
      mapa.get(nome).push(u.urf);
    }
    return mapa;
  }, [urfs]);
  const codigosDasUrfs = useCallback(
    (nomes) => (nomes || []).flatMap((n) => urfsPorNome.get(n) || [n]),
    [urfsPorNome]
  );
  const unidadeQtd = unidadeDaQuantidade(commoditiesSelecionadas);

  const tiposDaCommodity = useCallback(
    (commodity) =>
      tiposSelecionados
        .filter((valor) => valor.startsWith(`${commodity}${SEP_TIPO}`))
        .map((valor) => valor.slice(commodity.length + SEP_TIPO.length)),
    [tiposSelecionados]
  );

  const buildChartTitle = useCallback(() => {
    const produto = commoditiesSelecionadas.length
      ? commoditiesSelecionadas
          .map((c) => {
            const tipos = tiposDaCommodity(c);
            return tipos.length ? `${c} (${tipos.join(", ")})` : c;
          })
          .join(somarCommodities ? " + " : " x ")
      : "Todos os Produtos";
    const tipoFluxo = fluxo || "Exportação + Importação";
    // Destino (país e/ou bloco) no título. Antes o país SUBSTITUÍA "Brasil"
    // ("Exportação China", como se fosse a exportação da China, e com o
    // nome em inglês do cadastro) e o bloco nem aparecia — o gráfico
    // filtrava certo, mas o título dizia outra coisa.
    const nomePt = (valor) => paises.find((p) => p.pais === valor)?.paisPt || valor;
    const nomesPaises = (paisSelecionado || []).map(nomePt);
    const partesDestino = [];
    if (nomesPaises.length) {
      partesDestino.push(nomesPaises.length > 2 ? `${nomesPaises.length} países` : nomesPaises.join(", "));
    }
    const grupos = [...(continenteSelecionado || []), ...(blocoSelecionado || [])];
    if (grupos.length) {
      partesDestino.push(grupos.length > 2 ? `${grupos.length} regiões de destino` : grupos.join(", "));
    }
    // Origem no Brasil (estado, região, URF): antes ficava fora do título,
    // e o gráfico de "Sul" ou "Paranaguá" parecia o do Brasil inteiro.
    const partesOrigem = [];
    const ufs = ufSelecionada || [];
    if (ufs.length) partesOrigem.push(ufs.length > 3 ? `${ufs.length} estados` : ufs.join(", "));
    if (regiaoSelecionada) partesOrigem.push(`Região ${regiaoSelecionada}`);
    const nomesUrf = (urfSelecionada || []).map(nomeBonito);
    if (nomesUrf.length) partesOrigem.push(nomesUrf.length > 2 ? `${nomesUrf.length} URFs` : nomesUrf.join(", "));
    const brasil = partesOrigem.length ? `Brasil (${partesOrigem.join(" – ")})` : "Brasil";

    if (!partesDestino.length) return `${produto} - ${tipoFluxo} ${brasil}`;
    const destino = partesDestino.join(" – ");
    const rota =
      fluxo === "Exportação" ? `${brasil} → ${destino}` : fluxo === "Importação" ? `${destino} → ${brasil}` : `${brasil} ↔ ${destino}`;
    return `${produto} - ${tipoFluxo} ${rota}`;
  }, [
    commoditiesSelecionadas, tiposDaCommodity, fluxo, paisSelecionado, blocoSelecionado, continenteSelecionado,
    ufSelecionada, regiaoSelecionada, urfSelecionada, paises, somarCommodities,
  ]);

  // Escala única do gráfico. Rótulos, eixo, resumo e o código para
  // incorporar leem daqui — é o que mantém o número e a unidade contando
  // a mesma história.
  const escala = useMemo(
    () => escalaDosDados(chartData, metrica, moeda, unidadeQtd),
    [chartData, metrica, moeda, unidadeQtd]
  );

  const buildChartSubtitle = useCallback((met = metrica, esc = escala) => {
    // A grandeza (mil/t, milhão/t, US$ milhões...) vem da escala do
    // gráfico, não de um texto fixo: com o rótulo do ponto já limpo, é a
    // legenda que diz de que tamanho é o número.
    const base = { valor_fob_usd: "Valor FOB", quilograma_liquido: "Volume",
      quantidade_estatistica: "Quantidade Estatística", valor_medio_ton: "Valor Médio",
      valor_medio_qtd: "Valor Médio" }[met] || "";
    const metricaLabel = esc.unidade ? `${base} (${esc.unidade})` : base;
    const mesLabel = mesSelecionado && mesSelecionado.length > 0
      ? mesSelecionado.length === 1
        ? `Mês de ${months.find((m) => m.value === mesSelecionado[0])?.label || ""}`
        : `Meses: ${mesSelecionado.map((v) => months.find((m) => m.value === v)?.short || v).join(", ")}`
      : "Todos os Meses";
    return `${metricaLabel} - ${mesLabel}`;
  }, [metrica, mesSelecionado, escala]);

  // O ranking reaproveita os filtros já escolhidos aqui.
  const filtrosRanking = useMemo(
    () => ({
      anoInicio,
      anoFim,
      meses: mesSelecionado,
      fluxo,
      metrica,
      moeda,
      commodities: commoditiesSelecionadas,
      tiposDaCommodity,
      paises: paisSelecionado,
      ufs: ufSelecionada,
      urfs: codigosDasUrfs(urfSelecionada),
      bloco: blocoSelecionado,
      continentes: continenteSelecionado,
      regiao: regiaoSelecionada,
      unidadeQtd,
      produto: commoditiesSelecionadas.length
        ? commoditiesSelecionadas
            .map((c) => {
              const tipos = tiposDaCommodity(c);
              return tipos.length ? `${c} (${tipos.join(", ")})` : c;
            })
            .join(" + ")
        : "Todos os Produtos",
    }),
    [
      anoInicio, anoFim, mesSelecionado, fluxo, metrica, moeda, commoditiesSelecionadas,
      tiposDaCommodity, paisSelecionado, ufSelecionada, urfSelecionada, blocoSelecionado, regiaoSelecionada,
      continenteSelecionado, codigosDasUrfs, unidadeQtd,
    ]
  );

  const handleSearch = async () => {
    if (!anoInicio || !anoFim) {
      toast.error("Selecione o período (ano início e fim)!");
      return;
    }

    if (pacote && variacoesPacote.length === 0) {
      toast.error("Marque ao menos uma variação do pacote!");
      return;
    }

    setIsLoading(true);
    setChartData(null);
    setPacoteGraficos(null);

    try {
      const params = new URLSearchParams();
      params.append("ano_inicio", anoInicio);
      params.append("ano_fim", anoFim);
      if (fluxo) params.append("fluxo", fluxo);
      if (paisSelecionado && paisSelecionado.length > 0) paisSelecionado.forEach((p) => params.append("paises", p));
      if (ufSelecionada && ufSelecionada.length > 0) ufSelecionada.forEach((u) => params.append("uf", u));
      (blocoSelecionado || []).forEach((b) => params.append("bloco", b));
      (continenteSelecionado || []).forEach((c) => params.append("continente", c));
      if (regiaoSelecionada) params.append("regiao", regiaoSelecionada);
      codigosDasUrfs(urfSelecionada).forEach((u) => params.append("urf", u));

      // A consulta agregada aceita uma commodity por vez: uma chamada por
      // commodity, em paralelo, cada uma com os próprios tipos. Sem nenhuma
      // selecionada, uma chamada só com todos os produtos.
      const consultas = (commoditiesSelecionadas.length ? commoditiesSelecionadas : [null]).map((commodity) => {
        const p = new URLSearchParams(params);
        if (commodity) {
          p.append("commodity", commodity);
          tiposDaCommodity(commodity).forEach((t) => p.append("tipo", t));
        }
        return request(`/comex-consulta/dados/agregado?${p.toString()}`, { method: "GET" });
      });

      const needsBrl = pacote
        ? variacoesPacote.some((c) => VARIACOES_PACOTE.find((v) => v.chave === c)?.moeda === "brl")
        : moeda === "brl" && ["valor_fob_usd", "valor_medio_ton", "valor_medio_qtd"].includes(metrica);
      const respostas = await Promise.all(consultas);
      const nomes = commoditiesSelecionadas.length ? commoditiesSelecionadas : ["Todos os Produtos"];
      const comDados = nomes
        .map((nome, i) => ({ nome, linhas: respostas[i] || [] }))
        .filter((r) => r.linhas.length > 0);

      if (comDados.length === 0) {
        toast.warning("Nenhum dado encontrado para os filtros selecionados.");
        setIsLoading(false);
        return;
      }
      const semDados = nomes.filter((nome) => !comDados.some((r) => r.nome === nome));
      if (semDados.length) {
        toast.warning(`Sem dados no período para: ${semDados.join(", ")}.`);
      }

      // A API já devolve o FOB em reais com o dólar médio do próprio mês
      // (total_fob_brl). Mês sem cotação (a tabela começa em 2000) fica de
      // fora do gráfico em R$, em vez de sair em dólar com rótulo de real.
      if (needsBrl) {
        const semDolar = new Set(
          comDados.flatMap((r) => r.linhas.filter((l) => l.total_fob_brl == null).map((l) => `${l.mes}/${l.ano}`))
        );
        if (semDolar.size) {
          toast.warning(
            `Sem cotação do dólar para ${semDolar.size} ${semDolar.size === 1 ? "mês" : "meses"} ` +
              `(${[...semDolar].slice(0, 3).join(", ")}${semDolar.size > 3 ? "…" : ""}): fora do gráfico em R$.`
          );
        }
      }

      // Processar dados para o gráfico
      ultimaBusca.current = { comDados, selecao: commoditiesSelecionadas.join("|") };
      if (pacote) setPacoteGraficos(montarPacote(ultimaBusca.current, somarCommodities));
      else setChartData(montarDados(ultimaBusca.current, somarCommodities));
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      toast.error("Erro ao buscar dados. Verifique os filtros e tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Soma: junta as LINHAS de todas as commodities e processa uma vez só, como
   * se fosse uma commodity. Somar as séries já prontas daria errado no valor
   * médio por tonelada — a média do conjunto é FOB total / peso total, não a
   * soma das médias. As commodities não compartilham NCM, então nada é
   * contado duas vezes.
   */
  const montarDados = (busca, somar, met = metrica, moe = moeda) =>
    somar && busca.comDados.length > 1
      ? processChartData(busca.comDados.flatMap((r) => r.linhas), met, moe)
      : combinarCommodities(busca.comDados.map((r) => ({ nome: r.nome, dados: processChartData(r.linhas, met, moe) })));

  // Um gráfico do pacote por variação marcada, todos da mesma consulta.
  const montarPacote = (busca, somar, chaves = variacoesPacote) =>
    VARIACOES_PACOTE.filter((v) => chaves.includes(v.chave)).map((v) => {
      const met = metricaNaBase(v.metrica, baseVolume);
      const dados = montarDados(busca, somar, met, v.moeda);
      const porQtd = met !== v.metrica;
      return {
        ...v,
        metrica: met,
        rotulo: porQtd ? v.rotulo.replace("Volume", "Quantidade") + (met === "valor_medio_qtd" ? ` por ${unidadeQtd}` : "") : v.rotulo,
        dados,
        escala: escalaDosDados(dados, met, v.moeda, unidadeQtd),
        stats: getStats(dados),
      };
    });

  // Devolve os dados de UMA consulta; quem junta commodities é combinarCommodities.
  // Métrica e moeda vêm por parâmetro: no pacote, a mesma consulta vira
  // vários gráficos (volume, US$, R$, médio US$/t e R$/t).
  const processChartData = (rawData, met = metrica, moe = moeda) => {
    // Valor médio: por tonelada (FOB ÷ peso) ou por unidade da quantidade
    // estatística (FOB ÷ cabeças, no caso dos animais vivos).
    const isMediaTon = met === "valor_medio_ton" || met === "valor_medio_qtd";
    const chaveBase = met === "valor_medio_qtd" ? "total_quantidade" : "total_kg_liquido";
    const fatorBase = met === "valor_medio_qtd" ? 1 : 1000;
    const metricKey =
      met === "valor_fob_usd" || isMediaTon
        ? "total_fob_usd"
        : met === "quilograma_liquido"
        ? "total_kg_liquido"
        : "total_quantidade";

    // Em R$: cada mês com o dólar médio dele, já convertido pela API. O mês
    // sem cotação sai inteiro (FOB e peso), para não distorcer o R$/t.
    const isBrl = moe === "brl" && (met === "valor_fob_usd" || isMediaTon);
    const data = isBrl
      ? rawData
          .filter((d) => d.total_fob_brl != null)
          .map((d) => ({ ...d, total_fob_usd: d.total_fob_brl }))
      : rawData;

    // Helper: valor médio (USD ou BRL ÷ toneladas, ou ÷ quantidade)
    const computeMediaTon = (totalUsd, totalBase) => {
      if (!totalBase || totalBase === 0) return 0;
      return (totalUsd / totalBase) * fatorBase;
    };

    if (mesSelecionado && mesSelecionado.length > 0) {
      // Filtrar pelos meses selecionados e agrupar por ano
      const mesesNum = mesSelecionado.map(Number);
      const filtered = data.filter((d) => mesesNum.includes(Number(d.mes)));

      if (isMediaTon) {
        const groupedUsd = {}, groupedKg = {};
        filtered.forEach((d) => {
          if (!groupedUsd[d.ano]) { groupedUsd[d.ano] = 0; groupedKg[d.ano] = 0; }
          groupedUsd[d.ano] += d.total_fob_usd || 0;
          groupedKg[d.ano] += d[chaveBase] || 0;
        });
        const sortedAnos = Object.keys(groupedUsd).sort();
        const values = sortedAnos.map((ano) => computeMediaTon(groupedUsd[ano], groupedKg[ano]));
        return { categories: sortedAnos, values, metricKey };
      } else {
        const grouped = {};
        filtered.forEach((d) => {
          if (!grouped[d.ano]) grouped[d.ano] = 0;
          grouped[d.ano] += d[metricKey] || 0;
        });
        const sortedAnos = Object.keys(grouped).sort();
        const values = sortedAnos.map((ano) => scaledValue(met, grouped[ano]));
        return { categories: sortedAnos, values, metricKey };
      }
    } else if (agrupar === "ano") {
      if (isMediaTon) {
        const groupedUsd = {}, groupedKg = {};
        data.forEach((d) => {
          if (!groupedUsd[d.ano]) { groupedUsd[d.ano] = 0; groupedKg[d.ano] = 0; }
          groupedUsd[d.ano] += d.total_fob_usd || 0;
          groupedKg[d.ano] += d[chaveBase] || 0;
        });
        const categories = Object.keys(groupedUsd).sort();
        const values = categories.map((ano) => computeMediaTon(groupedUsd[ano], groupedKg[ano]));
        return { categories, values, metricKey };
      } else {
        // Agrupar por ano
        const grouped = {};
        data.forEach((d) => {
          if (!grouped[d.ano]) grouped[d.ano] = 0;
          grouped[d.ano] += d[metricKey] || 0;
        });
        const categories = Object.keys(grouped).sort();
        const values = categories.map((ano) => scaledValue(met, grouped[ano]));
        return { categories, values, metricKey };
      }
    } else if (agrupar === "ano_mes") {
      // Agrupar por ano/mês — multi-series (uma série por ano, meses no eixo X)
      const anosPresentes = [...new Set(data.map((d) => d.ano))].sort();
      const categories = months.map((m) => m.short);

      if (isMediaTon) {
        const series = anosPresentes.map((ano) => {
          const dadosAno = data.filter((d) => Number(d.ano) === Number(ano));
          const valoresMes = months.map((m) => {
            const entries = dadosAno.filter((d) => Number(d.mes) === m.value);
            const totalUsd = entries.reduce((acc, d) => acc + (d.total_fob_usd || 0), 0);
            const totalKg = entries.reduce((acc, d) => acc + (d[chaveBase] || 0), 0);
            return computeMediaTon(totalUsd, totalKg);
          });
          return { name: String(ano), data: valoresMes };
        });
        return { categories, series, metricKey, multiSeries: true };
      } else {
        const series = anosPresentes.map((ano) => {
          const dadosAno = data.filter((d) => Number(d.ano) === Number(ano));
          const valoresMes = months.map((m) => {
            const total = dadosAno
              .filter((d) => Number(d.mes) === m.value)
              .reduce((acc, d) => acc + (d[metricKey] || 0), 0);
            return scaledValue(met, total);
          });
          return { name: String(ano), data: valoresMes };
        });
        return { categories, series, metricKey, multiSeries: true };
      }
    } else if (agrupar === "trimestre") {
      // Agrupar por trimestre — multi-series (uma série por ano, trimestres no eixo X)
      const trimestreLabels = ["1º Tri", "2º Tri", "3º Tri", "4º Tri"];
      const anosPresentes = [...new Set(data.map((d) => d.ano))].sort();

      if (isMediaTon) {
        const series = anosPresentes.map((ano) => {
          const dadosAno = data.filter((d) => Number(d.ano) === Number(ano));
          const valoresTri = [1, 2, 3, 4].map((tri) => {
            const mesesTri = [(tri - 1) * 3 + 1, (tri - 1) * 3 + 2, (tri - 1) * 3 + 3];
            const entries = dadosAno.filter((d) => mesesTri.includes(Number(d.mes)));
            const totalUsd = entries.reduce((acc, d) => acc + (d.total_fob_usd || 0), 0);
            const totalKg = entries.reduce((acc, d) => acc + (d[chaveBase] || 0), 0);
            return computeMediaTon(totalUsd, totalKg);
          });
          return { name: String(ano), data: valoresTri };
        });
        return { categories: trimestreLabels, series, metricKey, multiSeries: true };
      } else {
        const series = anosPresentes.map((ano) => {
          const dadosAno = data.filter((d) => Number(d.ano) === Number(ano));
          const valoresTri = [1, 2, 3, 4].map((tri) => {
            const mesesTri = [(tri - 1) * 3 + 1, (tri - 1) * 3 + 2, (tri - 1) * 3 + 3];
            const total = dadosAno
              .filter((d) => mesesTri.includes(Number(d.mes)))
              .reduce((acc, d) => acc + (d[metricKey] || 0), 0);
            return scaledValue(met, total);
          });
          return { name: String(ano), data: valoresTri };
        });
        return { categories: trimestreLabels, series, metricKey, multiSeries: true };
      }
    } else if (agrupar === "semestre") {
      // Agrupar por semestre — multi-series (uma série por ano, semestres no eixo X)
      const semestreLabels = ["1º Sem", "2º Sem"];
      const anosPresentes = [...new Set(data.map((d) => d.ano))].sort();

      if (isMediaTon) {
        const series = anosPresentes.map((ano) => {
          const dadosAno = data.filter((d) => Number(d.ano) === Number(ano));
          const valoresSem = [1, 2].map((sem) => {
            const mesesSem = sem === 1 ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
            const entries = dadosAno.filter((d) => mesesSem.includes(Number(d.mes)));
            const totalUsd = entries.reduce((acc, d) => acc + (d.total_fob_usd || 0), 0);
            const totalKg = entries.reduce((acc, d) => acc + (d[chaveBase] || 0), 0);
            return computeMediaTon(totalUsd, totalKg);
          });
          return { name: String(ano), data: valoresSem };
        });
        return { categories: semestreLabels, series, metricKey, multiSeries: true };
      } else {
        const series = anosPresentes.map((ano) => {
          const dadosAno = data.filter((d) => Number(d.ano) === Number(ano));
          const valoresSem = [1, 2].map((sem) => {
            const mesesSem = sem === 1 ? [1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12];
            const total = dadosAno
              .filter((d) => mesesSem.includes(Number(d.mes)))
              .reduce((acc, d) => acc + (d[metricKey] || 0), 0);
            return scaledValue(met, total);
          });
          return { name: String(ano), data: valoresSem };
        });
        return { categories: semestreLabels, series, metricKey, multiSeries: true };
      }
    } else {
      // Agrupar por mês — cronológico com rótulos Mês/Ano (ex: Jan/24)
      if (isMediaTon) {
        const pontos = [];
        data.forEach((d) => {
          const chave = `${d.ano}-${String(d.mes).padStart(2, "0")}`;
          const mesInfo = months.find((m) => m.value === Number(d.mes));
          const shortYear = String(d.ano).slice(-2);
          const label = `${mesInfo?.short || d.mes}/${shortYear}`;
          if (!pontos.find((p) => p.chave === chave)) {
            pontos.push({ chave, label, totalUsd: 0, totalKg: 0 });
          }
          const ponto = pontos.find((p) => p.chave === chave);
          ponto.totalUsd += d.total_fob_usd || 0;
          ponto.totalKg += d[chaveBase] || 0;
        });
        pontos.sort((a, b) => a.chave.localeCompare(b.chave));
        const categories = pontos.map((p) => p.label);
        const values = pontos.map((p) => computeMediaTon(p.totalUsd, p.totalKg));
        return { categories, values, metricKey, chaves: pontos.map((p) => p.chave) };
      } else {
        const pontos = [];
        data.forEach((d) => {
          const chave = `${d.ano}-${String(d.mes).padStart(2, "0")}`;
          const mesInfo = months.find((m) => m.value === Number(d.mes));
          const shortYear = String(d.ano).slice(-2);
          const label = `${mesInfo?.short || d.mes}/${shortYear}`;
          if (!pontos.find((p) => p.chave === chave)) {
            pontos.push({ chave, label, total: 0 });
          }
          const ponto = pontos.find((p) => p.chave === chave);
          ponto.total += d[metricKey] || 0;
        });
        pontos.sort((a, b) => a.chave.localeCompare(b.chave));
        const categories = pontos.map((p) => p.label);
        const values = pontos.map((p) => scaledValue(met, p.total));
        return { categories, values, metricKey, chaves: pontos.map((p) => p.chave) };
      }
    }
  };

  // Configuração do gráfico Highcharts (estilo similar ao modelo enviado)
  const varianteAtual = () => ({ dados: chartData, metrica, moeda, escala, stats });

  const getChartOptions = (variante = varianteAtual()) => {
    const { dados, metrica: met, escala: esc, stats: st } = variante;
    if (!dados) return {};

    const multiSeriesColors = [
      chartColor, "#00BFFF", "#FF6347", "#FFD700", "#FF69B4",
      "#00CED1", "#FF8C00", "#9370DB", "#20B2AA", "#DC143C",
      "#7FFF00", "#1E90FF", "#FF4500", "#ADFF2F", "#BA55D3",
    ];

    const isMulti = dados.multiSeries;
    const isBar = tipoGrafico === "bar";
    const isPie = tipoGrafico === "pie";
    const allValues = isMulti
      ? dados.series.flatMap((s) => s.data)
      : dados.values;
    const positiveValues = allValues.filter((v) => v > 0);
    const maxVal = Math.max(...allValues);
    const minVal = positiveValues.length > 0 ? Math.min(...positiveValues) : 0;
    const yMin = isBar ? 0 : Math.max(0, minVal - (maxVal - minVal) * 0.15);

    const chartType = isPie
      ? "pie"
      : isBar
      ? "column"
      : isMulti
      ? "spline"
      : "areaspline";

    // Dados do gráfico pizza (agrega multi-series por nome, ou usa categories/values)
    const pieSeriesData = isPie
      ? isMulti
        ? dados.series.map((s) => ({
            name: s.name,
            y: s.data.reduce((acc, v) => acc + v, 0),
          }))
        : (dados.categories || []).map((cat, i) => ({
            name: cat,
            y: dados.values[i],
          }))
      : [];

    return {
      chart: {
        type: chartType,
        backgroundColor: "#1a1a2e",
        style: { fontFamily: "Arial, sans-serif" },
        height: 360,
      },
      exporting: { enabled: false },
      title: {
        text: buildChartTitle(),
        style: { color: "#FFFFFF", fontSize: "20px", fontWeight: "bold" },
      },
      subtitle: {
        text: (() => {
          const varColor = st && st.variation >= 0 ? "#3f8600" : "#cf1322";
          const varSign  = st && st.variation >= 0 ? "+" : "";
          const varLine  = st
            ? `<div style="text-align:center;margin-top:4px;font-size:13px;font-weight:bold;color:${varColor};">Variação total: ${varSign}${st.variation}%</div>`
            : "";
          return (
            `<div style="text-align:center;color:#FFFFFF;">${buildChartSubtitle(met, esc)}</div>` +
            `<div style="text-align:center;color:#FFFFFF;font-size:11px;margin-top:2px;">Fonte: RuralBusiness/Comex</div>` +
            varLine
          );
        })(),
        useHTML: true,
        align: "center",
        style: { color: "#FFFFFF", fontSize: "15px", textAlign: "center" },
      },
      credits: { enabled: false },
      xAxis: isPie ? { visible: false } : {
        categories: dados.categories,
        labels: {
          enabled: !ocultarLegendaX,
          style: { color: "#FFFFFF", fontSize: "13px", fontWeight: "bold" },
          rotation: isMulti ? -45 : 0,
        },
        lineColor: "#444",
      },
      yAxis: isPie ? { visible: false } : {
        min: yMin,
        maxPadding: 0.22,
        title: { text: null },
        labels: {
          style: { color: "#FFFFFF" },
          formatter: function () {
            return formatarNaEscala(this.value, esc.divisor);
          },
        },
        gridLineColor: "rgba(255,255,255,0.08)",
      },
      legend: {
        enabled: isMulti || isPie,
        itemStyle: { color: "#FFFFFF", fontSize: "13px" },
        itemHoverStyle: { color: chartColor },
      },
      tooltip: {
        enabled: false,
        backgroundColor: "rgba(0,0,0,0.85)",
        borderColor: chartColor,
        style: { color: "#fff" },
        shared: isMulti && !isPie,
        formatter: isPie
          ? function () {
              const unit = esc.unidade;
              return (
                `<b>${this.point.name}</b><br/>` +
                `${formatarNaEscala(this.y, esc.divisor)} ${unit} (${this.percentage.toFixed(1)}%)`
              );
            }
          : isMulti
          ? undefined
          : function () {
              const unit = esc.unidade;
              return (
                `<b>${this.x}</b><br/>` +
                `${formatarNaEscala(this.y, esc.divisor)} ${unit}`
              );
            },
        pointFormat: isMulti && !isPie
          ? '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y:,.2f}</b><br/>'
          : undefined,
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",
          colors: [
            chartColor, "#00BFFF", "#FF6347", "#FFD700", "#FF69B4",
            "#00CED1", "#FF8C00", "#9370DB", "#20B2AA", "#DC143C",
            "#7FFF00", "#1E90FF", "#FF4500", "#ADFF2F", "#BA55D3",
          ],
          dataLabels: {
            enabled: true,
            format: "<b>{point.name}</b>: {point.percentage:.1f}%",
            style: { color: "#FFFFFF", fontSize: "13px", fontWeight: "bold", textOutline: "2px #1a1a2e" },
          },
          showInLegend: true,
          borderWidth: 2,
          borderColor: "#1a1a2e",
        },
        areaspline: {
          lineColor: chartColor,
          lineWidth: 3,
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, hexToRgba(chartColor, 0.4)],
              [1, hexToRgba(chartColor, 0.02)],
            ],
          },
          marker: {
            enabled: true,
            fillColor: chartColor,
            lineColor: "#FFFFFF",
            lineWidth: 2,
            radius: 6,
            symbol: "circle",
          },
          dataLabels: {
            enabled: true,
            useHTML: true,
            align: "center",
            verticalAlign: "top",
            y: -40,
            crop: false,
            overflow: "allow",
            allowOverlap: true,
            style: {
              color: "#FFFFFF",
              fontSize: "16px",
              fontWeight: "bold",
              textOutline: "none",
            },
            formatter: function () {
              const val = formatarNaEscala(this.y, esc.divisor);
              const pts = this.series.data;
              const prev = pts[this.point.index - 1]?.y;
              if (!prev || prev === 0) return `<div style="text-align:center;white-space:nowrap">${val}</div>`;
              const pct = ((this.y - prev) / Math.abs(prev) * 100).toFixed(1);
              const color = pct >= 0 ? "#39FF14" : "#FF4500";
              const sign = pct >= 0 ? "+" : "";
              return `<div style="text-align:center;white-space:nowrap;text-shadow:1px 1px 2px #000">${val} <span style="font-size:11px;color:${color}">${sign}${pct}%</span></div>`;
            },
          },
        },
        spline: {
          lineWidth: 2.5,
          marker: {
            enabled: true,
            lineColor: "#FFFFFF",
            lineWidth: 1,
            radius: 4,
            symbol: "circle",
          },
          dataLabels: {
            enabled: false,
          },
        },
        column: {
          borderWidth: 0,
          borderRadius: 4,
          groupPadding: 0.15,
          pointPadding: 0.05,
          dataLabels: {
            enabled: true,
            useHTML: true,
            align: "center",
            verticalAlign: "top",
            y: isMulti ? -18 : -22,
            crop: false,
            overflow: "allow",
            allowOverlap: true,
            style: {
              color: "#FFFFFF",
              // Com várias colunas por categoria não sobra largura para
              // valor + variação: os rótulos invadiam a coluna vizinha.
              fontSize: isMulti ? "12px" : "15px",
              fontWeight: "bold",
              textOutline: "none",
            },
            formatter: function () {
              const val = formatarNaEscala(this.y, esc.divisor);
              if (isMulti) return `<div style="text-align:center;white-space:nowrap;text-shadow:1px 1px 2px #000">${val}</div>`;
              const pts = this.series.data;
              const prev = pts[this.point.index - 1]?.y;
              if (!prev || prev === 0) return `<div style="text-align:center;white-space:nowrap">${val}</div>`;
              const pct = ((this.y - prev) / Math.abs(prev) * 100).toFixed(1);
              const color = pct >= 0 ? "#39FF14" : "#FF4500";
              const sign = pct >= 0 ? "+" : "";
              return `<div style="text-align:center;white-space:nowrap;text-shadow:1px 1px 2px #000">${val} <span style="font-size:11px;color:${color}">${sign}${pct}%</span></div>`;
            },
          },
        },
      },
      series: isPie
        ? [
            {
              name: buildChartTitle(),
              colorByPoint: true,
              data: pieSeriesData,
            },
          ]
        : isMulti
        ? dados.series.map((s, i) => ({
            name: s.name,
            data: s.data,
            color: multiSeriesColors[i % multiSeriesColors.length],
          }))
        : [
            {
              name: buildChartTitle(),
              data: dados.values,
              color: isBar ? {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [
                  [0, chartColor],
                  [1, hexToRgba(chartColor, 0.5)],
                ],
              } : chartColor,
            },
          ],
    };
  };

  // Estatísticas resumo
  function getStats(dados = chartData) {
    // Primeiro/último/variação comparariam pontos de commodities diferentes
    // (ex.: Soja 2021 contra Algodão 2026) — com mais de uma, não há resumo.
    if (!dados || dados.porCommodity) return null;
    const values = dados.multiSeries
      ? dados.series.flatMap((s) => s.data)
      : dados.values;
    if (!values || !values.length) return null;
    const total = values.reduce((a, b) => a + b, 0);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const first = values[0];
    const last = values[values.length - 1];
    const variation = first > 0 ? (((last - first) / first) * 100).toFixed(1) : 0;

    return { total, max, min, first, last, variation };
  };

  const stats = getStats();

  // Estado do modal de HTML
  const [htmlModalVisible, setHtmlModalVisible] = useState(false);
  const [generatedHtml, setGeneratedHtml] = useState("");

  const generateChartHtml = () => {
    if (!chartData) {
      toast.warning("Gere o gráfico antes de exportar o HTML.");
      return;
    }
    abrirHtml(montarIframe(varianteAtual()));
  };

  // Leva o gráfico (o da tela ou um do pacote) para o Gerador de imagens,
  // no padrão da casa — lá dá para trocar o modelo, ajustar e gerar o JPG.
  const abrirNoGerador = (variante) => {
    const spec = specDoGraficoComex({
      dados: variante.dados,
      titulo: buildChartTitle(),
      subtitulo: buildChartSubtitle(variante.metrica, variante.escala),
      escala: variante.escala,
      tipoGrafico,
      cor: chartColor,
    });
    if (!spec) {
      toast.warning("Não há dados para levar ao gerador.");
      return;
    }
    try {
      sessionStorage.setItem(CHAVE_SPEC_IMPORTADO, JSON.stringify(spec));
    } catch {}
    navigate(`/graficos/gerador${ehPopup ? "?popup=1" : ""}`);
  };

  const abrirHtml = (html) => {
    setGeneratedHtml(html);
    setHtmlModalVisible(true);
  };

  // O pacote inteiro, na ordem das variações: um iframe por gráfico,
  // separados por <br>, como nas matérias de alerta.
  const gerarHtmlPacote = () => {
    if (!pacoteGraficos?.length) return;
    abrirHtml(pacoteGraficos.map((g) => montarIframe(g)).join("<br>\n"));
  };

  // O código para incorporar de UM gráfico (o da tela ou um do pacote).
  const montarIframe = (variante) => {
    const { dados, metrica: met, moeda: moe, escala: esc } = variante;
    const title = buildChartTitle();
    const subtitle = buildChartSubtitle(met, esc);
    const isMulti = dados.multiSeries || false;
    const yAxisTitle = esc.unidade;

    const seriesForEmbed = isMulti
      ? dados.series
      : [{ name: title, data: dados.values || [] }];

    const embedData = {
      title,
      subtitle,
      yAxisTitle,
      divisor: esc.divisor,
      chartType: tipoGrafico,
      isMulti,
      categories: dados.categories || [],
      series: seriesForEmbed,
      metric: met,
      moeda: moe,
      ocultarLegendaX,
      color: chartColor,
    };

    const embedJson = JSON.stringify(embedData)
      .replace(/</g, "\\u003c")
      .replace(/>/g, "\\u003e")
      .replace(/&/g, "\\u0026")
      .replace(/\u2028/g, "\\u2028")
      .replace(/\u2029/g, "\\u2029");

    const embedDocument = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>* { margin: 0; padding: 0; box-sizing: border-box; } body { overflow: hidden; }</style>
</head>
<body>
  <div id="rb-comex-chart" style="width:100%;height:480px;"></div>
  <script id="rb-comex-data" type="application/json">${embedJson}</script>
  <script src="https://web.ruralbusiness.com.br/highcharts.js"></script>
  <script>
    (function () {
      var node = document.getElementById('rb-comex-data');
      if (!node || typeof Highcharts === 'undefined') return;
      var d = JSON.parse(node.textContent || '{}');
      var col = d.color || '#39FF14';

      function hexToRgba(hex, a) {
        var r=parseInt(hex.slice(1,3),16), g=parseInt(hex.slice(3,5),16), b=parseInt(hex.slice(5,7),16);
        return 'rgba('+r+','+g+','+b+','+a+')';
      }

      var multiColors = [col,'#00BFFF','#FF6347','#FFD700','#FF69B4','#00CED1','#FF8C00','#9370DB','#20B2AA','#DC143C','#7FFF00','#1E90FF','#FF4500','#ADFF2F','#BA55D3'];

      // Mesma escala da tela: o divisor e a unidade vêm prontos no objeto
      // de dados, para a imagem incorporada dizer exatamente o que o
      // gerador mostrou.
      function fmtEsc(v) {
        if (v == null) return '0';
        return (v / (d.divisor || 1)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      function fmtUnit() { return d.yAxisTitle || ''; }

      var isMulti = d.isMulti;
      var isBar = d.chartType === 'bar';
      var isPie = d.chartType === 'pie';
      var chartType = isPie ? 'pie' : isBar ? 'column' : isMulti ? 'spline' : 'areaspline';

      var allVals = d.series.reduce(function (acc, s) { return acc.concat(s.data); }, []);
      var maxV = allVals.length ? Math.max.apply(null, allVals) : 0;
      var posVals = allVals.filter(function (v) { return v > 0; });
      var minPosV = posVals.length ? Math.min.apply(null, posVals) : 0;
      var yMin = isBar ? 0 : Math.max(0, minPosV - (maxV - minPosV) * 0.15);

      var pieData = [];
      if (isPie) {
        pieData = isMulti
          ? d.series.map(function (s) { return { name: s.name, y: s.data.reduce(function (a, b) { return a + b; }, 0) }; })
          : (d.categories || []).map(function (cat, i) { return { name: cat, y: d.series[0].data[i] }; });
      }

      var series = isPie
        ? [{ name: d.title, colorByPoint: true, data: pieData }]
        : isMulti
          ? d.series.map(function (s, i) { return { name: s.name, data: s.data, color: multiColors[i % multiColors.length] }; })
          : [{ name: d.title, data: d.series[0].data, color: isBar ? { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, col], [1, hexToRgba(col, 0.5)]] } : col }];

      Highcharts.chart('rb-comex-chart', {
        chart: { type: chartType, backgroundColor: '#1a1a2e', style: { fontFamily: 'Arial,sans-serif' } },
        exporting: { enabled: false },
        title: { text: d.title, style: { color: '#FFF', fontSize: '18px', fontWeight: 'bold' } },
        subtitle: { text: d.subtitle, style: { color: '#FFD700', fontSize: '14px' } },
        credits: { enabled: true, text: 'Fonte: RURAL BUSINESS/ME', style: { color: '#aaa' } },
        xAxis: isPie ? { visible: false } : {
          categories: d.categories,
          labels: { enabled: !d.ocultarLegendaX, style: { color: '#FFF', fontSize: '13px', fontWeight: 'bold' }, rotation: isMulti ? -45 : 0 },
          lineColor: '#444'
        },
        yAxis: isPie ? { visible: false } : {
          min: yMin,
          maxPadding: 0.22,
          title: { text: d.yAxisTitle, style: { color: '#FFF' } },
          labels: { style: { color: '#FFF' }, formatter: function () { return fmtEsc(this.value); } },
          gridLineColor: 'rgba(255,255,255,0.08)'
        },
        legend: { enabled: isMulti || isPie, itemStyle: { color: '#FFF', fontSize: '13px' }, itemHoverStyle: { color: col } },
        tooltip: {
          enabled: false,
          backgroundColor: 'rgba(0,0,0,0.85)',
          borderColor: col,
          style: { color: '#fff' },
          shared: isMulti && !isPie,
          formatter: isPie
            ? function () { return '<b>' + this.point.name + '</b><br/>' + fmtEsc(this.y) + ' ' + fmtUnit() + ' (' + this.percentage.toFixed(1) + '%)'; }
            : (!isMulti ? function () { return '<b>' + this.x + '</b><br/>' + fmtEsc(this.y) + ' ' + fmtUnit(); } : undefined),
          pointFormat: (isMulti && !isPie) ? '\u25CF {series.name}: <b>{point.y:,.2f}</b><br/>' : undefined
        },
        plotOptions: {
          pie: {
            allowPointSelect: true, cursor: 'pointer', colors: multiColors,
            dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.percentage:.1f}%', style: { color: '#FFF', fontSize: '13px', fontWeight: 'bold', textOutline: '2px #1a1a2e' } },
            showInLegend: true, borderWidth: 2, borderColor: '#1a1a2e'
          },
          areaspline: {
            lineColor: col, lineWidth: 3,
            fillColor: { linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 }, stops: [[0, hexToRgba(col, 0.4)], [1, hexToRgba(col, 0.02)]] },
            marker: { enabled: true, fillColor: col, lineColor: '#FFF', lineWidth: 2, radius: 6, symbol: 'circle' },
            dataLabels: {
              enabled: true, useHTML: true, align: 'center', verticalAlign: 'top', y: -40, crop: false, overflow: 'allow', allowOverlap: true,
              style: { color: '#FFF', fontSize: '16px', fontWeight: 'bold', textOutline: 'none' },
              formatter: function () {
                var val = fmtEsc(this.y);
                var pts = this.series.data;
                var prev = pts[this.point.index - 1] ? pts[this.point.index - 1].y : null;
                if (!prev) return '<div style="text-align:center;white-space:nowrap">' + val + '</div>';
                var pct = ((this.y - prev) / Math.abs(prev) * 100).toFixed(1);
                var color = pct >= 0 ? '#39FF14' : '#FF4500';
                var sign = pct >= 0 ? '+' : '';
                return '<div style="text-align:center;white-space:nowrap;text-shadow:1px 1px 2px #000">' + val + ' <span style="font-size:11px;color:' + color + '">' + sign + pct + '%</span></div>';
              }
            }
          },
          spline: {
            lineWidth: 2.5,
            marker: { enabled: true, lineColor: '#FFF', lineWidth: 1, radius: 4, symbol: 'circle' },
            dataLabels: { enabled: false }
          },
          column: {
            borderWidth: 0, borderRadius: 4, groupPadding: 0.15, pointPadding: 0.05,
            dataLabels: {
              enabled: true, useHTML: true, align: 'center', verticalAlign: 'top', y: isMulti ? -18 : -22, crop: false, overflow: 'allow', allowOverlap: true,
              style: { color: '#FFF', fontSize: isMulti ? '12px' : '15px', fontWeight: 'bold', textOutline: 'none' },
              formatter: function () {
                var val = fmtEsc(this.y);
                if (isMulti) return '<div style="text-align:center;white-space:nowrap;text-shadow:1px 1px 2px #000">' + val + '</div>';
                var pts = this.series.data;
                var prev = pts[this.point.index - 1] ? pts[this.point.index - 1].y : null;
                if (!prev) return '<div style="text-align:center;white-space:nowrap">' + val + '</div>';
                var pct = ((this.y - prev) / Math.abs(prev) * 100).toFixed(1);
                var color = pct >= 0 ? '#39FF14' : '#FF4500';
                var sign = pct >= 0 ? '+' : '';
                return '<div style="text-align:center;white-space:nowrap;text-shadow:1px 1px 2px #000">' + val + ' <span style="font-size:11px;color:' + color + '">' + sign + pct + '%</span></div>';
              }
            }
          }
        },
        series: series
      });
    }());
  </script>
</body>
</html>`;

    const encodedDocument = encodeURIComponent(embedDocument);
    return `<iframe src="data:text/html;charset=utf-8,${encodedDocument}" width="100%" height="520" frameborder="0" scrolling="no" style="border:none;display:block;width:100%;"></iframe>`;
  };

  const copyHtmlToClipboard = () => {
    navigator.clipboard.writeText(generatedHtml).then(() => {
      toast.success("HTML copiado para a área de transferência!");
    }).catch(() => {
      toast.error("Erro ao copiar. Selecione manualmente.");
    });
  };

  return (
    <Content style={{ margin: "12px 16px" }}>
      <Title level={3}>
        <BarChartOutlined /> Gráficos COMEX - Importação / Exportação
      </Title>

      <Row gutter={[16, 16]}>
        {/* Painel de Filtros */}
        <Col xs={24} lg={7}>
          <Card title="Filtros" size="small" loading={loadingIndices}>
            <Form layout="vertical" form={form} size="small">
              <Form.Item label="Visão">
                <Radio.Group
                  value={visao}
                  onChange={(e) => setVisao(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="serie">Evolução</Radio.Button>
                  <Radio.Button value="ranking">Ranking</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {/* Período */}
              <Divider orientation="left" plain style={{ margin: "4px 0 12px" }}>
                Período
              </Divider>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="Ano Início">
                    <Select
                      value={anoInicio}
                      onChange={setAnoInicio}
                      options={years.map((y) => ({ value: y, label: y }))}
                      showSearch
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Ano Fim">
                    <Select
                      value={anoFim}
                      onChange={setAnoFim}
                      options={years.map((y) => ({ value: y, label: y }))}
                      showSearch
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Meses (opcional)">
                <Select
                  mode="multiple"
                  value={mesSelecionado}
                  onChange={setMesSelecionado}
                  allowClear
                  placeholder="Todos os meses"
                  options={months}
                  maxTagCount="responsive"
                />
              </Form.Item>

              {/* Fluxo */}
              <Form.Item label="Fluxo">
                <Radio.Group
                  value={fluxo}
                  onChange={(e) => setFluxo(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                >
                  {fluxoOptions.map((opt) => (
                    <Radio.Button key={opt.value} value={opt.value}>
                      {opt.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </Form.Item>

              {/* Pacote: várias variações numa consulta só */}
              {visao === "serie" && (
                <Form.Item style={{ marginBottom: pacote ? 4 : 12 }}>
                  <Checkbox
                    checked={pacote}
                    onChange={(e) => {
                      setPacote(e.target.checked);
                      // Cada modo mostra só o próprio resultado.
                      setPacoteGraficos(null);
                      setChartData(null);
                    }}
                  >
                    Gerar vários gráficos (pacote)
                  </Checkbox>
                </Form.Item>
              )}
              {visao === "serie" && pacote && (
                <Form.Item label="Variações do pacote">
                  <Checkbox.Group
                    value={variacoesPacote}
                    onChange={setVariacoesPacote}
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                    options={VARIACOES_PACOTE.map((v) => ({ value: v.chave, label: v.rotulo }))}
                  />
                </Form.Item>
              )}
              {visao === "serie" && pacote && (
                <Form.Item
                  label="Volume e valor médio por"
                  extra={baseVolume === "quantidade" ? `Volume em ${unidadeQtd === "cab" ? "cabeças" : "quantidade estatística"} e valor médio por ${unidadeQtd}.` : null}
                >
                  <Radio.Group
                    value={baseVolume}
                    onChange={(e) => setBaseVolume(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="peso">Peso (t)</Radio.Button>
                    <Radio.Button value="quantidade">Quantidade (cab/un)</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              )}

              {/* Métrica (no pacote, cada variação traz a sua) */}
              {!(visao === "serie" && pacote) && (
              <Form.Item label="Métrica">
                <Select
                  value={metrica}
                  onChange={setMetrica}
                  options={metricOptions}
                />
              </Form.Item>
              )}

              {/* Moeda (apenas para Valor FOB e Valor Médio/ton) */}
              {!(visao === "serie" && pacote) && ["valor_fob_usd", "valor_medio_ton", "valor_medio_qtd"].includes(metrica) && (
                <Form.Item label="Moeda">
                  <Radio.Group
                    value={moeda}
                    onChange={(e) => setMoeda(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="usd">US$ (Dólar)</Radio.Button>
                    <Radio.Button value="brl">R$ (Real)</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              )}

              <Divider orientation="left" plain style={{ margin: "4px 0 12px" }}>
                Filtros Avançados
              </Divider>

              {/* País */}
              <Form.Item label="País">
                <Select
                  mode="multiple"
                  value={paisSelecionado}
                  onChange={setPaisSelecionado}
                  allowClear
                  showSearch
                  placeholder="Todos os países"
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  options={[
                    ...new Map(
                      paises.map((p) => [p.pais, { value: p.pais, label: p.paisPt || p.pais }])
                    ).values(),
                  ]}
                />
              </Form.Item>

              {/* Continente (completo: a Ásia inclui o Oriente Médio) */}
              <Form.Item label="Continente">
                <Select
                  mode="multiple"
                  value={continenteSelecionado}
                  onChange={setContinenteSelecionado}
                  allowClear
                  showSearch
                  maxTagCount="responsive"
                  placeholder="Todos os continentes"
                  options={continentesUnicos.map((c) => ({ value: c, label: c }))}
                />
              </Form.Item>

              {/* Bloco */}
              <Form.Item label="Bloco Econômico">
                <Select
                  mode="multiple"
                  value={blocoSelecionado}
                  onChange={setBlocoSelecionado}
                  allowClear
                  showSearch
                  maxTagCount="responsive"
                  placeholder="Todos os blocos"
                  options={blocosUnicos.map((b) => ({ value: b, label: b }))}
                />
              </Form.Item>

              {/* UF */}
              <Form.Item label="Estado (UF)">
                <Select
                  mode="multiple"
                  value={ufSelecionada}
                  onChange={setUfSelecionada}
                  allowClear
                  showSearch
                  placeholder="Todos os estados"
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  options={[
                    ...new Map(
                      estados.map((e) => [
                        e.uf,
                        { value: e.uf, label: `${e.uf} - ${e.estado_correto || e.estado}` },
                      ])
                    ).values(),
                  ]}
                />
              </Form.Item>

              {/* Região */}
              <Form.Item label="Região">
                <Select
                  value={regiaoSelecionada}
                  onChange={setRegiaoSelecionada}
                  allowClear
                  showSearch
                  placeholder="Todas as regiões"
                  options={regioesUnicas.map((r) => ({ value: r, label: r }))}
                />
              </Form.Item>

              {/* URF */}
              <Form.Item label="URF (Unidade Receita Federal)">
                <Select
                  mode="multiple"
                  value={urfSelecionada}
                  onChange={setUrfSelecionada}
                  allowClear
                  showSearch
                  placeholder="Todas as URFs"
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  options={emOrdem(urfsPorNome.keys()).map((nome) => ({ value: nome, label: nomeBonito(nome) }))}
                />
              </Form.Item>

              {/* Commodity/Produto */}
              <Form.Item
                label={`Commodities (até ${MAX_COMMODITIES})`}
                extra={
                  commoditiesSelecionadas.length > 1
                    ? somarCommodities
                      ? "As commodities são somadas numa série só."
                      : "Cada commodity vira uma série no gráfico."
                    : null
                }
              >
                <Select
                  mode="multiple"
                  maxCount={MAX_COMMODITIES}
                  value={commoditiesSelecionadas}
                  onChange={(vals) => {
                    setCommoditiesSelecionadas(vals);
                    // Tipos de commodity retirada da seleção deixam de valer.
                    setTiposSelecionados((atuais) =>
                      atuais.filter((valor) => vals.some((c) => valor.startsWith(`${c}${SEP_TIPO}`)))
                    );
                  }}
                  allowClear
                  showSearch
                  placeholder="Todas as commodities"
                  optionFilterProp="label"
                  options={[
                    ...new Map(ncms.map((n) => [n.commodity, { value: n.commodity, label: n.commodity }])).values(),
                  ]}
                />
              </Form.Item>
              {commoditiesSelecionadas.length > 1 && (
                <Checkbox
                  checked={somarCommodities}
                  style={{ marginTop: -12, marginBottom: 12 }}
                  onChange={(e) => {
                    const somar = e.target.checked;
                    setSomarCommodities(somar);
                    // Refaz o gráfico já exibido, sem nova consulta — só se ele é
                    // desta mesma seleção; senão vale no próximo "Gerar Gráfico".
                    if (ultimaBusca.current?.selecao === commoditiesSelecionadas.join("|")) {
                      if (pacoteGraficos) setPacoteGraficos(montarPacote(ultimaBusca.current, somar));
                      else setChartData(montarDados(ultimaBusca.current, somar));
                    }
                  }}
                >
                  Somar as commodities selecionadas
                </Checkbox>
              )}

              {/* Tipos, agrupados por commodity */}
              {(() => {
                const grupos = commoditiesSelecionadas
                  .map((commodity) => ({
                    label: commodity,
                    options: [
                      ...new Set(
                        ncms
                          .filter((n) => n.commodity === commodity && n.tipo && n.tipo !== n.commodity)
                          .map((n) => n.tipo)
                      ),
                    ].map((tipo) => ({
                      value: `${commodity}${SEP_TIPO}${tipo}`,
                      label: commoditiesSelecionadas.length > 1 ? `${commodity}: ${tipo}` : tipo,
                    })),
                  }))
                  .filter((g) => g.options.length > 0);
                return grupos.length > 0 ? (
                  <Form.Item label="Tipo">
                    <Select
                      mode="multiple"
                      value={tiposSelecionados}
                      onChange={setTiposSelecionados}
                      allowClear
                      placeholder="Todos os tipos"
                      maxTagCount="responsive"
                      optionFilterProp="label"
                      options={grupos}
                    />
                  </Form.Item>
                ) : null;
              })()}

              {visao === "serie" && (
                <>
              {/* Tipo de Gráfico */}
              <Form.Item label="Tipo de Gráfico">
                <Radio.Group
                  value={tipoGrafico}
                  onChange={(e) => setTipoGrafico(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="area">Área</Radio.Button>
                  <Radio.Button value="bar">Barras</Radio.Button>
                  <Radio.Button value="pie">Pizza</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {/* Cor do Gráfico */}
              <Form.Item label="Cor do Gráfico">
                <Space>
                  <input
                    type="color"
                    value={chartColor}
                    onChange={(e) => setChartColor(e.target.value)}
                    style={{ width: 40, height: 28, padding: 2, cursor: "pointer", borderRadius: 4, border: "1px solid #d9d9d9" }}
                  />
                  <span style={{ fontSize: 12, fontFamily: "monospace" }}>{chartColor.toUpperCase()}</span>
                  <Button size="small" onClick={() => setChartColor("#39FF14")}>Reset</Button>
                </Space>
              </Form.Item>

              <Checkbox
                checked={ocultarLegendaX}
                onChange={(e) => setOcultarLegendaX(e.target.checked)}
              >
                Ocultar legenda do eixo X
              </Checkbox>

              <Form.Item label="Largura do Gráfico" style={{ marginTop: 12 }}>
                <Radio.Group
                  value={chartCols}
                  onChange={(e) => setChartCols(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value={11}>P</Radio.Button>
                  <Radio.Button value={13}>M</Radio.Button>
                  <Radio.Button value={17}>G</Radio.Button>
                  <Radio.Button value={24}>Max</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {/* Agrupamento */}
              {(!mesSelecionado || mesSelecionado.length === 0) && (
                <Form.Item label="Agrupar por">
                  <Radio.Group
                    value={agrupar}
                    onChange={(e) => setAgrupar(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="ano">Ano</Radio.Button>
                    <Radio.Button value="mes">Mês</Radio.Button>
                    <Radio.Button value="ano_mes">Ano/Mês</Radio.Button>
                    <Radio.Button value="trimestre">Trimestre</Radio.Button>
                    <Radio.Button value="semestre">Semestre</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              )}

                </>
              )}

              {visao === "serie" && (
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={isLoading}
                block
                size="middle"
                style={{ marginTop: 8 }}
              >
                {pacote ? "Gerar Gráficos" : "Gerar Gráfico"}
              </Button>
              )}
            </Form>
          </Card>
        </Col>

        {/* Ranking (imagem no formato da casa) */}
        {visao === "ranking" && (
          <Col xs={24} lg={24 - 7}>
            <PainelRankingComex filtros={filtrosRanking} ehPopup={ehPopup} />
          </Col>
        )}

        {/* Área do Gráfico */}
        {visao === "serie" && (
        <Col xs={24} lg={chartCols}>
          <Card
            size="small"
            styles={{ body: { padding: 0 } }}
            extra={
              pacoteGraficos ? (
                <Space>
                  <Button size="small" icon={<CodeOutlined />} onClick={gerarHtmlPacote} type="primary" ghost>
                    HTML do pacote ({pacoteGraficos.length})
                  </Button>
                  <Button size="small" icon={<ReloadOutlined />} onClick={handleSearch}>
                    Atualizar
                  </Button>
                </Space>
              ) : chartData && (
                <Space>
                  <Button
                    size="small"
                    icon={<CodeOutlined />}
                    onClick={generateChartHtml}
                    type="primary"
                    ghost
                  >
                    Gerar HTML
                  </Button>
                  <Button size="small" icon={<EditOutlined />} onClick={() => abrirNoGerador(varianteAtual())}>
                    Gerador de imagem
                  </Button>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={handleSearch}
                  >
                    Atualizar
                  </Button>
                </Space>
              )
            }
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 360,
                }}
              >
                <Spin size="large" tip="Carregando dados..." />
              </div>
            ) : pacoteGraficos ? (
              <div>
                {pacoteGraficos.map((g) => (
                  <div key={g.chave} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        background: "#fafafa",
                      }}
                    >
                      <Text strong>{g.rotulo}</Text>
                      <Space>
                        <Button size="small" icon={<CodeOutlined />} onClick={() => abrirHtml(montarIframe(g))}>
                          Gerar HTML
                        </Button>
                        <Button size="small" icon={<EditOutlined />} onClick={() => abrirNoGerador(g)}>
                          Gerador de imagem
                        </Button>
                      </Space>
                    </div>
                    <HighchartsReact highcharts={Highcharts} options={getChartOptions(g)} />
                  </div>
                ))}
              </div>
            ) : chartData ? (
              <div>
                {/* Estatísticas */}
                {stats && (
                  <Row
                    gutter={16}
                    style={{ padding: "16px 24px", background: "#fafafa" }}
                  >
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title="Total"
                        value={formatarNaEscala(stats.total, escala.divisor)}
                        suffix={<span style={{ fontSize: 13 }}>{escala.unidade}</span>}
                        valueStyle={{ fontSize: 14 }}
                      />
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title="Máximo"
                        value={formatarNaEscala(stats.max, escala.divisor)}
                        suffix={<span style={{ fontSize: 13 }}>{escala.unidade}</span>}
                        valueStyle={{ fontSize: 14, color: "#3f8600" }}
                      />
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title="Mínimo"
                        value={formatarNaEscala(stats.min, escala.divisor)}
                        suffix={<span style={{ fontSize: 13 }}>{escala.unidade}</span>}
                        valueStyle={{ fontSize: 14, color: "#cf1322" }}
                      />
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title="Primeiro"
                        value={formatarNaEscala(stats.first, escala.divisor)}
                        suffix={<span style={{ fontSize: 13 }}>{escala.unidade}</span>}
                        valueStyle={{ fontSize: 14 }}
                      />
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title="Último"
                        value={formatarNaEscala(stats.last, escala.divisor)}
                        suffix={<span style={{ fontSize: 13 }}>{escala.unidade}</span>}
                        valueStyle={{ fontSize: 14 }}
                      />
                    </Col>
                    <Col xs={12} sm={8} md={4}>
                      <Statistic
                        title="Variação"
                        value={stats.variation}
                        suffix="%"
                        valueStyle={{
                          fontSize: 14,
                          color: stats.variation >= 0 ? "#3f8600" : "#cf1322",
                        }}
                      />
                    </Col>
                  </Row>
                )}
                <HighchartsReact
                  highcharts={Highcharts}
                  options={getChartOptions()}
                />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 360,
                  color: "#999",
                }}
              >
                <BarChartOutlined style={{ fontSize: 64, marginBottom: 16 }} />
                <Text type="secondary" style={{ fontSize: 16 }}>
                  Configure os filtros e clique em "Gerar Gráfico"
                </Text>
                <Text type="secondary" style={{ fontSize: 13, marginTop: 4 }}>
                  Dados de Importação e Exportação COMEX
                </Text>
              </div>
            )}
          </Card>
        </Col>
        )}
      </Row>

      {/* Modal HTML Gerado */}
      <Modal
        title="HTML do Gráfico"
        open={htmlModalVisible}
        onCancel={() => setHtmlModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setHtmlModalVisible(false)}>
            Fechar
          </Button>,
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={copyHtmlToClipboard}
          >
            Copiar HTML
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 8, color: "#666" }}>
          Cole este HTML no editor de notícias (Jodit) ou em qualquer página web.
          O gráfico é auto-contido e não depende do sistema.
        </p>
        <Input.TextArea
          value={generatedHtml}
          readOnly
          rows={16}
          style={{ fontFamily: "monospace", fontSize: 12 }}
        />
      </Modal>
    </Content>
  );
}
